import { MarketSelectableCard, MarketStatusPill } from '~/features/shared/market-content';
import type { B2BOpportunity, B2BOpportunityStatus, B2BTurn } from '../types';
import {
  LISTING_FILLED_LABEL,
  formatCurrency,
  formatRelativeTime,
  isListingFilled,
} from '../utils';

type Props = {
  opportunity: B2BOpportunity;
  isSelected: boolean;
  onSelect: (responseId: number) => void;
};

type StatusDisplay = {
  label: string;
  status: Parameters<typeof MarketStatusPill>[0]['status'];
  pulse?: boolean;
};

/**
 * Sidebar badge derived from backend-provided (status, turn).
 * Mirrors the table in Task 4 of the B2B Opportunities plan.
 * Never re-derive turn here — always consume `turn` from the API.
 */
export const getSidebarStatusDisplay = (
  status: B2BOpportunityStatus,
  turn: B2BTurn
): StatusDisplay => {
  switch (status) {
    case 'MESSAGED':
      return { label: 'Inquiry sent', status: 'neutral' };
    case 'APPLIED':
      return { label: 'Applied', status: 'neutral' };
    case 'NEGOTIATING':
      if (turn === 'YOUR_TURN') {
        return { label: 'Awaiting You', status: 'yourTurn', pulse: true };
      }
      return { label: 'Negotiating', status: 'pending' };
    case 'PENDING_ACCEPTANCE':
      if (turn === 'YOUR_TURN') {
        return { label: 'Awaiting You', status: 'yourTurn', pulse: true };
      }
      if (turn === 'THEIR_TURN') {
        return { label: 'Awaiting Counterparty', status: 'theirTurn' };
      }
      return { label: 'Pending Acceptance', status: 'pending' };
    case 'ACCEPTED':
      return { label: 'Accepted', status: 'accepted' };
    case 'REJECTED':
      return { label: 'Declined', status: 'danger' };
    case 'WITHDRAWN':
      return { label: 'Withdrawn', status: 'inactive' };
    default:
      return { label: status, status: 'neutral' };
  }
};

export const OpportunityCard = ({ opportunity, isSelected, onSelect }: Props) => {
  const { listing } = opportunity;
  // [MCV-1889] A response closed because its listing filled reads neutral, never as Declined.
  const statusDisplay: StatusDisplay = isListingFilled(opportunity)
    ? { label: LISTING_FILLED_LABEL, status: 'neutral' }
    : getSidebarStatusDisplay(opportunity.status, opportunity.turn);

  return (
    <MarketSelectableCard
      selected={isSelected}
      className="mb-3 shrink-0"
      onClick={() => onSelect(opportunity.responseId)}
      dataCy={`opportunity-card-${opportunity.responseId}`}
    >
      <div className="mb-2 flex min-w-0 flex-wrap items-center gap-2">
        <MarketStatusPill status="responded" label="B2B" />
        <span className="shrink-0 text-xs text-content-secondary tabular-nums @max-[280px]/sidebar:hidden">
          #{listing.id}
        </span>
      </div>

      <p
        className="body-text-sb mb-1 line-clamp-2 break-words leading-snug text-content-primary"
        data-cy="opportunity-title"
        title={listing.listingTitle}
      >
        {listing.listingTitle}
      </p>

      <div
        className="flex min-w-0 max-w-full flex-wrap items-center gap-1 overflow-hidden text-xs text-content-secondary"
        data-cy="opportunity-metadata"
      >
        {listing.specialtyName && (
          <span className="min-w-0 truncate" title={listing.specialtyName}>
            {listing.specialtyName}
          </span>
        )}
        {listing.stateCodes.length > 0 && (
          <>
            <span className="shrink-0">&middot;</span>
            <span className="shrink-0">
              {listing.stateCodes.length > 3
                ? `${listing.stateCodes.length} states`
                : listing.stateCodes.join(', ')}
            </span>
          </>
        )}
        {listing.totalContractValue != null && (
          <>
            <span className="shrink-0">&middot;</span>
            <span className="shrink-0">{formatCurrency(listing.totalContractValue)}</span>
          </>
        )}
      </div>

      <div className="mt-3 flex min-w-0 items-center justify-between gap-2 border-t border-grey-900 pt-2">
        <MarketStatusPill
          status={statusDisplay.status}
          label={statusDisplay.label}
          pulse={statusDisplay.pulse}
          cyTag={`opportunity-badge-${opportunity.responseId}`}
        />
        <span
          className="shrink-0 text-xs text-content-secondary @max-[280px]/sidebar:hidden"
          data-cy="opportunity-updated-at"
        >
          {formatRelativeTime(opportunity.updatedAt)}
        </span>
      </div>
    </MarketSelectableCard>
  );
};
